import {inject, Injectable} from '@angular/core';
import {TenantApplication} from '../../models/tenant/TenantApplication';
import {TenantApplicationAPI} from '../../api/models/tenant/TenantApplicationApi';
import {catchError, map, Observable, of, tap} from 'rxjs';
import {Tenant} from '../../models/tenant/Tenant';
import {TenantAPI} from '../../api/models/tenant/TenantApi';
import {CacheService} from '../cache/cache.service';
import {RequestCacheService} from '../cache/request-cache.service';
import {CloudHttpClient} from '../httpClient/CloudHttpClient';
import {TranslateService} from '@ngx-translate/core';
import {AuthHttpClient} from '../httpClient/AuthHttpClient';
import { AuthService } from '../auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class TenantService {
    private cloudHttpClient: CloudHttpClient = inject(CloudHttpClient);
    private authHttpClient: AuthHttpClient = inject(AuthHttpClient);

    private applicationsCacheService: CacheService<TenantApplication[]> = inject(CacheService); 
    private tenantsCacheService: CacheService<Tenant[]> = inject(CacheService);
    private authService: AuthService = inject(AuthService);

    private requestCacheService: RequestCacheService = inject(RequestCacheService);
    private translateService: TranslateService = inject(TranslateService);

    private static TENANT_APPLICATION_CACHE_KEY_PREFIX: string = "tenantApplications_";
    private static TENANT_CACHE_KEY_PREFIX: string = "tenants_";
    private static CACHE_EXPIRATION_TIME_IN_MINUTES: number = 24 * 60;

    constructor() { }

    public getTenant(tenantId: string): Observable<Tenant> {
        return this.getAllTenantsFromCurrentUser().pipe(
            map(tenants => {
                const tenant = tenants.find(t => t.id == tenantId);
                if (!tenant) {
                    throw new Error(`Tenant ${tenantId} not found`);
                }

                return tenant;
            }),
            catchError(() => of(new Tenant(tenantId, this.translateService.instant("error.tenant.unknown", {id: tenantId}))))
        )
    }

    public getAllTenantsFromCurrentUser(): Observable<Tenant[]> {
        const currentTenantId = this.authService.getTenantIdFromCurrentUser();
        const key = TenantService.TENANT_CACHE_KEY_PREFIX + (currentTenantId ?? "unknown");
        
        const cachedTenants: Tenant[] | undefined = this.tenantsCacheService.getData(key);
        if (!cachedTenants) {
            return this.requestCacheService.getOrAddRequestObservable(key, () => 
                this.retrieveAllTenantsFromCurrentUser().pipe(
                    tap(tenants => this.tenantsCacheService.saveData(tenants, key, TenantService.CACHE_EXPIRATION_TIME_IN_MINUTES))
                )
            )
        }
        return of(cachedTenants);
    }

    public getApplication(applicationId: string, tenantId: string): Observable<TenantApplication> {
        return this.getAllApplicationsForCurrentUser().pipe(
            map(applications => {
                const application = applications.find(ta => ta.id == applicationId && ta.tenantId == tenantId);
                if (!application) {
                    throw new Error(`Application ${applicationId} not found`);
                }

                return application;
            }),
            catchError(() => of(new TenantApplication(
                applicationId,
                tenantId,
                this.translateService.instant("error.tenant.unknown", {id: applicationId})
            )))
        )
    }

    public getAllApplicationsForCurrentUser(): Observable<TenantApplication[]> {
        const currentTenantId = this.authService.getTenantIdFromCurrentUser();
        const key = TenantService.TENANT_APPLICATION_CACHE_KEY_PREFIX + (currentTenantId ?? "unknown");

        const cachedApplications: TenantApplication[] | undefined = this.applicationsCacheService.getData(key);
        if (!cachedApplications) {
            return this.requestCacheService.getOrAddRequestObservable(key, () => 
                this.retrieveAllApplicationsFromCurrentUser().pipe(
                    tap(applications => this.applicationsCacheService.saveData(applications, key, TenantService.CACHE_EXPIRATION_TIME_IN_MINUTES))
                )
            )
        }
        return of(cachedApplications);
    }

    private retrieveAllApplicationsFromCurrentUser(): Observable<TenantApplication[]> {
        return this.cloudHttpClient.get<TenantApplicationAPI[]>("/tenant/application/currentUser").pipe(
            map((tenantApplications: TenantApplicationAPI[]) => tenantApplications.map(tenantApplication => new TenantApplication(
                    tenantApplication.id,
                    tenantApplication.tenantId,
                    tenantApplication.name,
                    tenantApplication.friendlyName,
                )
            ))
        );
    }

     private retrieveAllTenantsFromCurrentUser(): Observable<Tenant[]> {
        return this.authHttpClient.get<TenantAPI[]>("/api/tenant/allRelatedTenants/currentUser").pipe(
            map((tenants: TenantAPI[]) => tenants.map(tenant => new Tenant(
                    tenant.id,
                    tenant.name,
                )
            ))
        );
    }

    public getTotalAmountOfTenants(): Observable<number> {
        return this.authHttpClient.get<number>("/api/tenant/count");
    }

    public getTotalAmountOfTenantApplications(): Observable<number> {
        return this.cloudHttpClient.get<number>("/tenant/application/count");
    }
}
